import { motion } from "framer-motion";
import { Wallet, PiggyBank, Percent, CalendarClock, TrendingUp, ArrowRight, Sun } from "lucide-react";

/**
 * FinancialClarity — positioning section that dramatises what NZ Taxation
 * actually sells. Old school = tax returns, P&L, GST returns. New school =
 * "you know exactly what you can safely spend today". The visual is a mock
 * dashboard card echoing the reference the client sent, restyled navy/gold.
 */
export default function FinancialClarity() {
  return (
    <section
      id="clarity"
      className="relative bg-white border-b border-slate-200 overflow-hidden"
      data-testid="financial-clarity-section"
    >
      <div className="absolute inset-0 bg-grid opacity-40 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 grid md:grid-cols-12 gap-10 md:gap-14 items-center">
        {/* Left — the positioning story */}
        <div className="md:col-span-5">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold text-[#C9A349]">
            <TrendingUp className="w-3.5 h-3.5" /> What we actually sell
          </div>
          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="mt-4 font-heading font-black text-3xl md:text-5xl tracking-tight leading-[1.05] text-[#0A192F]"
          >
            We&apos;re not just preparing
            <br />
            tax returns.
            <br />
            <span className="text-[#C9A349]">We&apos;re delivering clarity.</span>
          </motion.h2>
          <p className="mt-5 text-slate-600 text-base md:text-lg leading-relaxed">
            Profit &amp; loss statements. Balance sheets. GST returns. Year-end accounts. Yes,
            we prepare all of them. They&apos;re essential parts of the job.
          </p>
          <p className="mt-4 text-slate-600 text-base md:text-lg leading-relaxed">
            But what our clients value most is something beyond compliance: clear, practical
            insight into how their business is really performing.
          </p>
          <p className="mt-4 text-slate-600 text-base md:text-lg leading-relaxed">
            Through <span className="font-heading font-bold text-[#0A192F]">ClientOps</span>,
            business owners get a live, easy-to-understand view of the numbers that matter,
            helping answer the question that keeps many NZ business owners awake at night:
          </p>
          <blockquote className="mt-6 border-l-4 border-[#C9A349] bg-[#FAF6EC] px-5 py-4">
            <p className="m-0 font-heading font-black text-lg md:text-2xl tracking-tight leading-tight text-[#0A192F]">
              &quot;Am I actually making progress?&quot;
            </p>
          </blockquote>
          <a
            href="#pricing"
            className="mt-8 inline-flex items-center gap-2 bg-[#0A192F] text-white px-6 py-4 font-heading font-bold uppercase tracking-[0.14em] text-sm hover:bg-[#C9A349] hover:text-[#0A192F] transition-colors"
            data-testid="clarity-cta"
          >
            Get your financial clarity <ArrowRight className="w-4 h-4" />
          </a>
        </div>

        {/* Right — mock dashboard card */}
        <div className="md:col-span-7">
          <MockDashboard />
        </div>
      </div>
    </section>
  );
}

function MockDashboard() {
  return (
    <div className="relative" data-testid="clarity-dashboard-mock">
      {/* Gold glow behind card */}
      <div
        aria-hidden
        className="absolute -inset-6 md:-inset-10 bg-[#C9A349]/10 blur-3xl pointer-events-none"
      />

      <div className="relative bg-white border border-slate-200 shadow-[0_30px_60px_-15px_rgba(10,25,47,0.25)] p-5 md:p-7">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-400">
              Client dashboard
            </div>
            <div className="mt-1 font-heading font-black text-lg text-[#0A192F]">
              Dashboard
            </div>
          </div>
          <div className="inline-flex items-center gap-2 text-xs text-slate-500">
            <Sun className="w-3.5 h-3.5 text-[#C9A349]" />
            <span>Good morning, Michael</span>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          {/* Safe to Spend hero card */}
          <div className="col-span-3 md:col-span-1 bg-[#0A192F] text-white p-5 flex flex-col justify-center items-center text-center">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#C9A349]">
              Safe to spend
            </div>
            <div className="mt-2 font-heading font-black text-4xl md:text-5xl tracking-tight tabular-nums">
              $3,420
            </div>
            <div className="mt-2 text-[11px] uppercase tracking-[0.18em] text-slate-300">
              Updated today
            </div>
          </div>

          {/* 2×2 metric grid */}
          <div className="col-span-3 md:col-span-2 grid grid-cols-2 gap-3">
            <MetricCard icon={Wallet} label="Bank balance" value="$12,650" />
            <MetricCard icon={PiggyBank} label="Tax reserve" value="$4,250" />
            <MetricCard icon={Percent} label="GST reserve" value="$1,980" />
            <MetricCard icon={CalendarClock} label="Upcoming bills" value="$1,000" sublabel="2 due" />
          </div>
        </div>

        {/* Progress bars */}
        <div className="mt-5 grid md:grid-cols-2 gap-4">
          <ProgressRow label="Income tax funding" tag="End of year estimate" percent={78} />
          <ProgressRow label="GST funding" tag="Next return" percent={100} />
        </div>
      </div>

      <div className="mt-5 text-center text-[10px] uppercase tracking-[0.24em] font-bold text-slate-400">
        <span className="inline-flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-[#C9A349] animate-pulse" /> Live client view · sample data
        </span>
      </div>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, sublabel }) {
  return (
    <div className="border border-slate-200 p-4 flex items-center gap-3">
      <div className="w-9 h-9 flex items-center justify-center bg-[#C9A349]/15 text-[#0A192F]">
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 truncate">
          {label}
        </div>
        <div className="font-heading font-black text-lg text-[#0A192F] tabular-nums">
          {value}
        </div>
        {sublabel && (
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400">{sublabel}</div>
        )}
      </div>
    </div>
  );
}

function ProgressRow({ label, tag, percent }) {
  const onTrack = percent >= 75;
  return (
    <div className="border border-slate-200 p-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-heading font-bold text-sm text-[#0A192F]">{label}</div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400">{tag}</div>
        </div>
        <div className="font-heading font-black text-lg text-[#0A192F] tabular-nums">
          {percent}%
        </div>
      </div>
      <div className="mt-3 h-1.5 w-full bg-slate-100 overflow-hidden">
        <div
          className="h-full bg-[#C9A349]"
          style={{ width: `${percent}%` }}
        />
      </div>
      <div className="mt-2 flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] font-bold text-[#0A192F]">
        <span className={`w-1.5 h-1.5 rounded-full ${onTrack ? "bg-emerald-500" : "bg-amber-500"}`} />
        {onTrack ? "On track" : "Watch this"}
      </div>
    </div>
  );
}
